import { forwardRef, useEffect, useRef, useState } from 'react'
import MapGL, { NavigationControl } from 'react-map-gl/mapbox'

type MapboxMapProps = React.ComponentProps<typeof MapGL> & {
  mapImages?: Array<{ name: string; url: string }>
  hideNavigationControl?: boolean
  setIsStyleLoaded: (loaded: boolean) => void
}

export const MapboxMap = forwardRef<mapboxgl.Map, MapboxMapProps>((props, ref) => {
  const { children, onLoad, mapImages = [], hideNavigationControl = false, setIsStyleLoaded, ...rest } = props
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [hasLoadedForCurrentStyle, setHasLoadedForCurrentStyle] = useState<boolean>(false)

  useEffect(() => {
    const map = typeof ref !== 'function' && ref?.current
    if (!map) return

    const resizeObserver = new ResizeObserver(() => {
      map?.resize()
    })
    if (mapContainerRef?.current) resizeObserver.observe(mapContainerRef.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [ref, mapContainerRef?.current])

  function onMapLoad(e: mapboxgl.MapEvent) {
    const map = e.target || (typeof ref !== 'function' && ref?.current)
    if (!map) return

    const loadImagesIfNeeded = async () => {
      // Prevent multiple loads for the same style
      if (hasLoadedForCurrentStyle) return

      // Check which images are missing
      const missingImages = mapImages.filter((item) => !map.hasImage(item.name))

      if (missingImages.length === 0) {
        setHasLoadedForCurrentStyle(true)
        return // All images already loaded
      }

      await Promise.all(
        missingImages.map(async (item: { name: string; url: string }) => {
          return new Promise<void>((resolve, reject) => {
            map.loadImage(item.url, (error, image) => {
              if (error) {
                console.error('Error loading image:', error)
                reject(error)
                return
              }

              // Double-check it's still missing (async operation)
              if (!map.hasImage(item.name) && image) {
                map.addImage(item.name, image)
              }
              resolve()
            })
          })
        })
      )

      setHasLoadedForCurrentStyle(true)
    }

    const handleStyleData = async () => {
      setHasLoadedForCurrentStyle(false)
      await loadImagesIfNeeded()
      map.triggerRepaint()
      setIsStyleLoaded(true)
    }

    // Load images when style changes
    map.on('style.load', handleStyleData)
    handleStyleData()

    if (onLoad) {
      onLoad(e)
    }
  }

  return (
    <div ref={mapContainerRef} style={{ display: 'flex', flex: 1 }}>
      <MapGL
        ref={(r: React.ComponentRef<typeof MapGL>) => {
          const mapInstance = r?.getMap() || null
          if (typeof ref === 'function') {
            ref(mapInstance)
          } else if (ref && 'current' in ref) {
            ref.current = mapInstance
          }
        }}
        mapboxAccessToken='pk.eyJ1IjoiaG9zdG1hc3Rlci1ncmF2aXRhdGUiLCJhIjoiY21kZXY4OXMzMDZwZTJqbzlqb3J6eHVwYiJ9.7UiEKqOCTnrq1096tiKy7g'
        projection='mercator'
        attributionControl={false}
        onLoad={onMapLoad}
        {...rest}
      >
        {children}
        {!hideNavigationControl && <NavigationControl position='bottom-right' showCompass={false} />}
      </MapGL>
    </div>
  )
})
